/**
 * Registered Engines (0.6.0 license sync).
 *
 * Surfaces every Vloud engine that's ever self-registered with the
 * cloud. Each row carries machine_id, install_id, engine_version,
 * first/last_seen timestamps, and any currently-bound active
 * license — so an admin can:
 *
 *   1. See which engines exist on customer hardware (the data the
 *      pre-0.6 cloud was completely missing).
 *   2. Click "Issue trial" on a row to mint a trial license bound
 *      to that machine_id. The engine's pull worker picks it up
 *      within 60 seconds.
 *   3. See which bound licences end soon (C03-T18). A bound licence
 *      that lapses drops its engine to the Free Plan within a minute;
 *      the control-plane host's own licence once ran out with nothing
 *      anywhere saying so.
 *
 * Layout mirrors Servers.tsx so admins encounter a consistent UI.
 */
import { useEffect, useMemo, useState, type JSX } from 'react';
import { Link } from 'react-router-dom';
import { Cpu, RefreshCw, KeyRound, ExternalLink, CheckCircle2, X, Link2, Ban, Copy } from 'lucide-react';
import {
  AdminApiClient, 
  type EngineRow, type IssueEngineTrialBody, type EngineTunnelUrlResponse,
} from '../api';
import { userMessageFor, failureFrom, type Failure } from '../lib/api-state';
import { usePermissions } from '../lib/permissions';
import { AccessNotice } from '../components/ui/AccessNotice';
import { useSession } from '../session';
import { PageHeader } from '../components/ui/PageHeader';
import { HeroPanel, HeroKpi } from '../components/ui/HeroPanel';
import { Button } from '../components/ui/Button';
import { StatusChip, type Band } from '../components/ui/StatusChip';
import { DataTable, PrimaryWithSub, type Column } from '../components/ui/DataTable';
import { Drawer } from '../components/ui/Drawer';
import { bandForLiveness, labelForLiveness, fmtContactAge, isLive } from '../lib/liveness';

// VF-35: no local freshness rule here. The license-server returns `liveness` / `last_contact_at` (freshest of the
// 60 s licence pull, the registration refresh and the heartbeat) and the Servers page renders the same verdict.

/** Licences ending within this many days are listed above the table. */
export const EXPIRY_WINDOW_DAYS = 14;
const DAY_MS = 86_400_000;

export interface UpcomingExpiry {
  engine: EngineRow;
  endsAt: Date;
  msLeft: number;
}

/** Engines whose bound licence ends within `windowDays` (or already has), soonest first. */
export function upcomingExpiries(
  rows: readonly EngineRow[],
  now: number = Date.now(),
  windowDays: number = EXPIRY_WINDOW_DAYS,
): UpcomingExpiry[] {
  const out: UpcomingExpiry[] = [];
  for (const engine of rows) {
    if (!engine.bound_license_id || !engine.bound_license_expires_at) continue;
    const endsAt = new Date(engine.bound_license_expires_at);
    const msLeft = endsAt.getTime() - now;
    if (msLeft <= windowDays * DAY_MS) out.push({ engine, endsAt, msLeft });
  }
  return out.sort((a, b) => a.msLeft - b.msLeft);
}

/** Same bands as the licence server's sweep: ≤3 d critical, ≤14 d warning. */
export function expiryBand(msLeft: number): Band {
  if (msLeft <= 3 * DAY_MS) return 'critical';
  if (msLeft <= EXPIRY_WINDOW_DAYS * DAY_MS) return 'warning';
  return 'neutral';
}

export function describeTimeLeft(msLeft: number): string {
  if (msLeft <= 0) return 'ended';
  const hours = Math.floor(msLeft / 3_600_000);
  if (hours < 48) return `ends in ${hours}h`;
  return `ends in ${Math.floor(msLeft / DAY_MS)}d`;
}

function engineLabel(e: EngineRow): string {
  const mid = e.machine_id ?? '';
  return e.hostname ?? e.fqdn ?? e.public_ip ?? (mid.length > 16 ? `${mid.slice(0, 16)}…` : mid);
}

export function EnginesPage(): JSX.Element {
  const { state } = useSession();
  const [rows, setRows] = useState<EngineRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // Failure to LOAD the engine list is separate from a failed action: the
  // list must never fall back to the "no engines registered" empty state.
  const [loadErr, setLoadErr] = useState<Failure | null>(null);
  const [version, setVersion] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [issueFor, setIssueFor] = useState<EngineRow | null>(null);
  // The minted URL, held only long enough to show it. The server stores a
  // hash, so if this is dismissed without copying, the link is gone and a new
  // one must be issued — hence the modal rather than a toast.
  const [mintedUrl, setMintedUrl] = useState<EngineTunnelUrlResponse | null>(null);
  // Distinguishes 'just created' from 're-opened', which changes both the
  // dialog title and whether copying now is the last chance.
  const [urlRevealed, setUrlRevealed] = useState(false);
  const [busyMachine, setBusyMachine] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const api = useMemo(
    () => new AdminApiClient({ baseUrl: '', getToken: () => state.token }),
    [state.token],
  );
  const perms = usePermissions();
  const canIssue = perms.canWrite;

  async function createUrl(engine: EngineRow): Promise<void> {
    setBusyMachine(engine.machine_id);
    setErr(null);
    try {
      // ttl_seconds: null asks for a URL with no expiry. Omitting the field
      // would silently take the server default of 24 hours instead.
      const res = await api.createEngineTunnelUrl(engine.machine_id, { ttl_seconds: null });
      setUrlRevealed(false);
      setMintedUrl(res);
      setCopied(false);
      setVersion((v) => v + 1);
    } catch (e) {
      setErr(userMessageFor(e));
    } finally {
      setBusyMachine(null);
    }
  }

  /**
   * Re-open a URL that was already minted.
   *
   * Two calls rather than one: the list says WHICH links exist and whether
   * each can still be shown, and only then is the credential asked for. A
   * single endpoint handing back every URL would disclose secrets to anyone
   * who merely opened the page.
   */
  async function showUrl(engine: EngineRow): Promise<void> {
    setBusyMachine(engine.machine_id);
    setErr(null);
    try {
      const { tokens } = await api.listEngineTunnelTokens(engine.machine_id);
      const target = tokens.find((t) => t.revealable);
      if (!target) {
        setErr(tokens.length
          ? 'This link was created before URL recovery was enabled, so it cannot be shown again. Revoke it and create a new one.'
          : 'This engine has no live public URL.');
        return;
      }
      const res = await api.revealEngineTunnelUrl(engine.machine_id, target.id);
      setUrlRevealed(true);
      setCopied(false);
      setMintedUrl({
        ok: true,
        url: res.url,
        token_prefix: target.token_prefix,
        expires_at: target.expires_at,
        never_expires: target.never_expires,
        recoverable: true,
        tunnel_connected: engine.tunnel_connected === true,
      });
    } catch (e) {
      setErr(userMessageFor(e));
    } finally {
      setBusyMachine(null);
    }
  }

  /**
   * Kick a tunnel that is up but misbehaving. The client redials in about a
   * second, so this is the "turn it off and on again" that does not involve
   * touching the customer's box.
   */
  async function reconnect(engine: EngineRow): Promise<void> {
    setBusyMachine(engine.machine_id);
    setErr(null);
    try {
      await api.reconnectEngineTunnel(engine.machine_id);
      // The relay needs a moment to accept the redial before the row would
      // show Live again, so refresh slightly after rather than immediately.
      setTimeout(() => setVersion((v) => v + 1), 2000);
    } catch (e) {
      setErr(userMessageFor(e));
    } finally {
      setBusyMachine(null);
    }
  }

  async function revokeUrl(engine: EngineRow): Promise<void> {
    // Revoking cuts off anyone currently using the link, so it is confirmed.
    // Creating one is not — that grants nothing until the URL is shared.
    if (!window.confirm(
      `Revoke every public URL for ${engine.machine_id.slice(0, 16)}…?\n\n`
      + 'Anyone using one right now loses access immediately.')) return;
    setBusyMachine(engine.machine_id);
    setErr(null);
    try {
      await api.revokeEngineTunnelUrls(engine.machine_id);
      setVersion((v) => v + 1);
    } catch (e) {
      setErr(userMessageFor(e));
    } finally {
      setBusyMachine(null);
    }
  }

  /**
   * Identity-key actions (engine-auth-protocol.md). The enrollment code is
   * shown once in a prompt so it can be copied; the server stores only its hash.
   */
  async function enrollmentCode(engine: EngineRow): Promise<void> {
    setBusyMachine(engine.machine_id);
    setErr(null);
    try {
      const r = await api.createEngineEnrollmentCode(engine.machine_id);
      window.prompt(
        `One-time enrollment code for ${engine.machine_id.slice(0, 16)}… (valid 24 h, shown once).\n`
        + 'Set it as VLOUD_ENROLLMENT_CODE on the engine, then restart it:', r.enrollment_code);
    } catch (e) {
      setErr(userMessageFor(e));
    } finally {
      setBusyMachine(null);
    }
  }

  async function resetKey(engine: EngineRow): Promise<void> {
    if (!window.confirm(
      `Reset the identity key of ${engine.machine_id.slice(0, 16)}…?\n\n`
      + 'The engine stops receiving its licence until it enrols a new key.')) return;
    setBusyMachine(engine.machine_id);
    setErr(null);
    try {
      await api.resetEngineKey(engine.machine_id);
      setVersion((v) => v + 1);
    } catch (e) {
      setErr(userMessageFor(e));
    } finally {
      setBusyMachine(null);
    }
  }

  async function clearQuarantine(engine: EngineRow): Promise<void> {
    if (!window.confirm(
      `Clear the clone quarantine on ${engine.machine_id.slice(0, 16)}…?\n\n`
      + 'Only do this once you are sure the identity key runs on ONE machine. '
      + 'Use "Reset key" instead if a copy may exist.')) return;
    setBusyMachine(engine.machine_id);
    setErr(null);
    try {
      await api.clearEngineQuarantine(engine.machine_id);
      setVersion((v) => v + 1);
    } catch (e) {
      setErr(userMessageFor(e));
    } finally {
      setBusyMachine(null);
    }
  }

  useEffect(() => {
    setRefreshing(true);
    api.engines()
      .then((r) => { setRows(r.engines); setErr(null); setLoadErr(null); })
      .catch((e) => { setRows(null); setLoadErr(failureFrom(e, 'the engine list')); })
      .finally(() => setRefreshing(false));
  }, [api, version]);

  const stats = useMemo(() => {
    const list = rows ?? [];
    const total    = list.length;
    const licensed = list.filter((e) => e.bound_license_id).length;
    const recent   = list.filter((e) => e.liveness === 'online').length;
    const stale    = list.filter((e) => !isLive(e.liveness)).length;
    return { total, licensed, recent, stale };
  }, [rows]);

  const expiring = useMemo(() => upcomingExpiries(rows ?? []), [rows]);

  const columns: Column<EngineRow>[] = [
    {
      key: 'identity',
      header: 'Engine',
      cell: (e) => {
        const mid = e.machine_id ?? '';
        const primary = e.hostname
          ?? e.fqdn
          ?? (e.public_ip ? `${e.public_ip}` : null)
          ?? (mid.length > 16 ? `${mid.slice(0, 16)}…` : (mid || '(no machine_id)'));
        const subBits = [
          e.engine_version ?? 'version unknown',
          e.public_ip && primary !== e.public_ip ? e.public_ip : null,
          e.os_distro ? `${e.os_distro}${e.os_version ? ` ${e.os_version}` : ''}` : null,
        ].filter(Boolean).join(' · ');
        return (
          <PrimaryWithSub
            primary={primary}
            sub={subBits}
          />
        );
      },
    },
    {
      key: 'machine_id',
      header: 'Machine ID',
      cell: (e) => (
        <span
          style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12, color: '#94a3b8' }}
          title={e.machine_id}
        >
          {e.machine_id ? `${e.machine_id.slice(0, 12)}…` : '—'}
        </span>
      ),
    },
    {
      key: 'key_id',
      header: 'Identity key',
      cell: (e) => (
        <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 2 }}>
          {e.clone_suspected_at ? <StatusChip band="critical">Clone suspected</StatusChip> : null}
          {e.key_id
            ? (
              <span
                style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12, color: '#94a3b8' }}
                title={`key ${e.key_id}${e.first_seen_ip ? ` · first seen from ${e.first_seen_ip}` : ''}`}
              >
                {e.key_id.slice(0, 10)}…
              </span>
            )
            : <span style={{ color: '#64748b', fontSize: 12 }}>not enrolled (legacy)</span>}
        </span>
      ),
    },
    {
      key: 'last_seen',
      header: 'Last contact',
      cell: (e) => {
        if (e.liveness === undefined && !e.last_seen_at) {
          return <span style={{ color: '#64748b', fontSize: 13 }}>—</span>;
        }
        const label = e.liveness === 'online' || e.liveness === 'degraded'
          ? fmtContactAge(e.contact_age_seconds)
          : `${labelForLiveness(e.liveness)}${e.contact_age_seconds != null ? ` · ${fmtContactAge(e.contact_age_seconds)}` : ''}`;
        return (
          <span title={`${labelForLiveness(e.liveness)}; registered/refreshed ${e.last_seen_at ?? 'never'}${e.last_auth_at ? `; last pull ${e.last_auth_at}` : ''}`}>
            <StatusChip band={bandForLiveness(e.liveness)}>{label}</StatusChip>
          </span>
        );
      },
    },
    {
      key: 'license',
      header: 'License',
      cell: (e) =>
        e.bound_license_id ? (
          <Link to={`/licenses/${e.bound_license_id}`} style={{ color: '#60a5fa', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <KeyRound size={13} />
            <span style={{ textTransform: 'capitalize' }}>{e.bound_license_tier ?? e.bound_license_status}</span>
            {e.bound_license_type === 'trial' ? <span style={{ fontSize: 11, color: '#fb923c' }}>(trial)</span> : null}
            {e.bound_license_type === 'offline' ? <span style={{ fontSize: 11, color: '#94a3b8' }}>(offline)</span> : null}
            {e.bound_license_expires_at ? (() => {
              const msLeft = new Date(e.bound_license_expires_at).getTime() - Date.now();
              return msLeft <= 30 * DAY_MS ? (
                <StatusChip band={expiryBand(msLeft)}>{describeTimeLeft(msLeft)}</StatusChip>
              ) : null;
            })() : null}
          </Link>
        ) : (
          <span style={{ color: '#64748b', fontSize: 13 }}>unbound</span>
        ),
    },
    {
      key: 'public_url',
      header: 'Public URL',
      // Two independent facts, and conflating them is how this column becomes
      // confusing: whether the engine is REACHABLE (holding a tunnel) and
      // whether anyone is ALLOWED in (a live token). An engine can be
      // connected with no token — that is the normal, closed state.
      cell: (e) => {
        if (e.tunnel_token_count && e.tunnel_token_count > 0) {
          return (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <StatusChip band={e.tunnel_connected ? 'healthy' : 'warning'}>
                {e.tunnel_connected ? 'Live' : 'Offline'}
              </StatusChip>
              <span style={{ color: '#94a3b8', fontSize: 12 }}>
                {e.tunnel_token_count} link{e.tunnel_token_count === 1 ? '' : 's'}
                {e.tunnel_never_expires
                  ? ' · no expiry'
                  : e.tunnel_expires_at
                    ? ` · until ${new Date(e.tunnel_expires_at).toLocaleDateString()}`
                    : ''}
              </span>
            </span>
          );
        }
        if (e.tunnel_connected) {
          return <span style={{ color: '#64748b', fontSize: 12 }}>connected · no link</span>;
        }
        return <span style={{ color: '#64748b' }}>—</span>;
      },
    },
    {
      key: 'actions',
      header: '',
      cell: (e) => (
        <span style={{ display: 'inline-flex', gap: 8, justifyContent: 'flex-end' }}>
          {e.clone_suspected_at ? (
            <Button size="sm" variant="destructive" disabled={!canIssue || busyMachine === e.machine_id} onClick={() => clearQuarantine(e)}>
              Clear quarantine
            </Button>
          ) : null}
          {e.key_id ? (
            <>
              <Button size="sm" variant="outline" disabled={!canIssue || busyMachine === e.machine_id} onClick={() => enrollmentCode(e)}>
                Enrollment code
              </Button>
              <Button size="sm" variant="outline" disabled={!canIssue || busyMachine === e.machine_id} onClick={() => resetKey(e)}>
                Reset key
              </Button>
            </>
          ) : null}
          {!e.bound_license_id && (
            <Button size="sm" disabled={!canIssue} onClick={() => setIssueFor(e)}>
              Issue trial
            </Button>
          )}
          {e.tunnel_token_count && e.tunnel_token_count > 0 ? (
            <>
              <Button
                size="sm"
                variant="outline"
                disabled={!canIssue || busyMachine === e.machine_id}
                onClick={() => showUrl(e)}
              >
                <Link2 size={14} />
                {busyMachine === e.machine_id ? 'Working…' : 'Show URL'}
              </Button>
              {e.tunnel_connected && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!canIssue || busyMachine === e.machine_id}
                  onClick={() => reconnect(e)}
                >
                  <RefreshCw size={14} />
                  {busyMachine === e.machine_id ? 'Working…' : 'Reconnect'}
                </Button>
              )}
              <Button
                size="sm"
                variant="destructive"
                disabled={!canIssue || busyMachine === e.machine_id}
                onClick={() => revokeUrl(e)}
              >
                <Ban size={14} />
                {busyMachine === e.machine_id ? 'Revoking…' : 'Revoke URL'}
              </Button>
            </>
          ) : (
            <Button
              size="sm"
              variant="outline"
              disabled={!canIssue || busyMachine === e.machine_id}
              onClick={() => createUrl(e)}
            >
              <Link2 size={14} />
              {busyMachine === e.machine_id ? 'Creating…' : 'Public URL'}
            </Button>
          )}
        </span>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        icon={Cpu}
        title="Registered engines"
        subtitle="Vloud engines that have self-registered with the cloud (0.6.0+). Issue a trial directly to any unbound engine."
        actions={
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setVersion((v) => v + 1)}
            disabled={refreshing}
          >
            <RefreshCw size={14} /> Refresh
          </Button>
        }
      />

      <AccessNotice reason={canIssue ? null : `${perms.writeReason ?? ''} Issuing trials and managing public URLs is not available to you.`} />

      {rows && <HeroPanel
        title="Engines"
        icon={Cpu}
        kpis={
          <>
            <HeroKpi label="Total"    value={stats.total} />
            <HeroKpi label="Licensed" value={stats.licensed} band={stats.licensed > 0 ? 'healthy' : 'neutral'} />
            <HeroKpi label="Online" value={stats.recent}   band={stats.recent > 0 ? 'healthy' : 'neutral'} />
            <HeroKpi label="Offline"  value={stats.stale}    band={stats.stale > 0 ? 'critical' : 'neutral'} />
            <HeroKpi
              label={`Licence ends ≤ ${EXPIRY_WINDOW_DAYS}d`}
              value={expiring.length}
              band={expiring.length > 0 ? (expiring.some((x) => x.msLeft <= 3 * DAY_MS) ? 'critical' : 'warning') : 'neutral'}
            />
          </>
        }
      />}

      {expiring.length > 0 ? (
        <section
          aria-label="Upcoming licence expiries"
          style={{
            marginTop: 16, padding: 12, borderRadius: 8,
            border: '1px solid rgba(245, 158, 11, 0.35)', background: 'rgba(245, 158, 11, 0.08)',
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 600, color: '#fbbf24', marginBottom: 8 }}>
            Licences ending in the next {EXPIRY_WINDOW_DAYS} days
          </div>
          <p style={{ margin: '0 0 8px', fontSize: 12, color: '#94a3b8' }}>
            When a bound licence ends, its engine drops to the Free Plan within a minute. Extend or
            re-issue it from the licence page before then.
          </p>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 6 }}>
            {expiring.map(({ engine, endsAt, msLeft }) => (
              <li key={engine.machine_id} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}>
                <StatusChip band={expiryBand(msLeft)}>{describeTimeLeft(msLeft)}</StatusChip>
                <span style={{ color: '#e2e8f0' }}>{engineLabel(engine)}</span>
                <span style={{ color: '#94a3b8' }}>
                  {[engine.bound_license_tier, engine.bound_license_type].filter(Boolean).join(' · ')}
                  {` · ${endsAt.toLocaleString()}`}
                </span>
                <Link to={`/licenses/${engine.bound_license_id}`} style={{ color: '#60a5fa', textDecoration: 'none' }}>
                  Open licence
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {err ? (
        <div role="alert" style={{ padding: 12, background: '#7f1d1d', color: '#fecaca', borderRadius: 6, marginTop: 16 }}>
          {err}
        </div>
      ) : null}

      <DataTable
        rows={rows ?? []}
        columns={columns}
        rowKey={(e, i) => e.machine_id ?? `idx-${i}`}
        loading={rows === null && !loadErr}
        error={loadErr}
        onRetry={() => setVersion((v) => v + 1)}
        emptyTitle="No engines have registered yet"
        emptyDescription="When a Vloud engine boots with VLOUD_LICENSE_SERVER_URL pointing here, it self-registers via POST /v1/engines/register and appears in this list within seconds."
      />

      {issueFor ? (
        <IssueTrialDrawer
          engine={issueFor}
          api={api}
          onClose={() => setIssueFor(null)}
          onDone={() => { setIssueFor(null); setVersion((v) => v + 1); }}
        />
      ) : null}

      {mintedUrl ? (
        <Drawer
          open
          title={urlRevealed ? 'Public URL' : 'Public URL created'}
          onClose={() => setMintedUrl(null)}
        >
          <div style={{ display: 'grid', gap: 14 }}>
            {/* The old copy asserted "shown once" unconditionally. That is
                only true where no encryption key is configured, so say which
                situation the operator is actually in rather than a default. */}
            <p style={{ margin: 0, color: '#e2e8f0', fontSize: 13, lineHeight: 1.6 }}>
              {mintedUrl.recoverable
                ? 'Anyone with this link reaches the engine\u2019s control panel. You can reopen it later with Show URL; revoke it when it is no longer needed.'
                : 'Copy this now \u2014 it is shown once. We store only a hash, so it cannot be displayed again. If it is lost, revoke and create a new one.'}
            </p>

            <div style={{
              display: 'flex', gap: 8, alignItems: 'center',
              background: '#0b1220', border: '1px solid #1e293b',
              borderRadius: 8, padding: '10px 12px',
            }}>
              <code style={{
                flex: 1, color: '#7dd3fc', fontSize: 12,
                wordBreak: 'break-all', fontFamily: 'ui-monospace, monospace',
              }}>{mintedUrl.url}</code>
              <Button
                size="sm"
                onClick={() => {
                  void navigator.clipboard.writeText(mintedUrl.url)
                    .then(() => setCopied(true))
                    .catch(() => setCopied(false));
                }}
              >
                {copied ? <><CheckCircle2 size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
              </Button>
            </div>

            <div style={{ color: '#94a3b8', fontSize: 12, lineHeight: 1.7 }}>
              {mintedUrl.never_expires || !mintedUrl.expires_at ? (
                // A standing credential, so say what ends it. Without this the
                // operator has no cue that Revoke URL is the only off switch.
                <div>
                  Never expires — it stays valid until you revoke it.
                </div>
              ) : (
                <div>Expires {new Date(mintedUrl.expires_at).toLocaleString()}</div>
              )}
              {!mintedUrl.tunnel_connected && (
                // Worth calling out rather than letting the operator open a
                // 503 and assume the feature is broken.
                <div style={{ color: '#fbbf24', marginTop: 6 }}>
                  This engine has not connected to the relay yet. The link will
                  return &ldquo;not connected&rdquo; until it dials in.
                </div>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button variant="outline" onClick={() => setMintedUrl(null)}>Done</Button>
              <a href={mintedUrl.url} target="_blank" rel="noreferrer">
                <Button>Open <ExternalLink size={13} /></Button>
              </a>
            </div>
          </div>
        </Drawer>
      ) : null}
    </>
  );
}

function IssueTrialDrawer({
  engine, api, onClose, onDone,
}: {
  engine: EngineRow;
  api: AdminApiClient;
  onClose: () => void;
  onDone: () => void;
}) {
  const [companyName, setCompanyName] = useState('');
  const [primaryEmail, setPrimaryEmail] = useState('');
  const [planTier, setPlanTier] = useState<'starter' | 'business' | 'enterprise'>('starter');
  const [trialDays, setTrialDays] = useState(30);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<{ activation_key: string; expires_at: string } | null>(null);

  async function submit() {
    setSubmitting(true);
    setErr(null);
    try {
      const body: IssueEngineTrialBody = {
        plan_tier: planTier,
        trial_days: trialDays,
      };
      if (companyName.trim())  body.company_name  = companyName.trim();
      if (primaryEmail.trim()) body.primary_email = primaryEmail.trim();
      const res = await api.issueEngineTrial(engine.machine_id, body);
      setDone({ activation_key: res.activation_key, expires_at: res.expires_at });
    } catch (e) {
      setErr(userMessageFor(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Drawer
      open={true}
      title={done ? 'Trial issued' : `Issue trial — ${engine.machine_id.slice(0, 16)}…`}
      onClose={onClose}
    >
      {done ? (
        <div style={{ padding: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <CheckCircle2 size={18} color="#10b981" />
            <div style={{ fontSize: 14 }}>
              The engine's pull worker will discover this license within 60 seconds.
              The dashboard sidebar will flip from "Waiting for cloud…" to "Active".
            </div>
          </div>
          <Field label="Activation key" value={done.activation_key} />
          <Field label="Expires"        value={new Date(done.expires_at).toLocaleString()} />
          <div style={{ marginTop: 16 }}>
            <Button onClick={onDone}>Done</Button>
          </div>
        </div>
      ) : (
        <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Input label="Company name (optional)"  value={companyName}   onChange={setCompanyName}  placeholder="Acme Corp" />
          <Input label="Primary email (optional)" value={primaryEmail}  onChange={setPrimaryEmail} placeholder="contact@example.com" type="email" />
          <Select label="Plan tier" value={planTier} onChange={(v) => setPlanTier(v as typeof planTier)}>
            <option value="starter">Starter</option>
            <option value="business">Business</option>
            <option value="enterprise">Enterprise</option>
          </Select>
          <Input label="Trial days" value={String(trialDays)} onChange={(v) => setTrialDays(Math.max(1, Math.min(90, Number(v) || 30)))} type="number" />

          {err ? <div style={{ padding: 10, background: '#7f1d1d', color: '#fecaca', borderRadius: 4 }}>{err}</div> : null}

          <div style={{ display: 'flex', gap: 8 }}>
            <Button onClick={submit} disabled={submitting}>{submitting ? 'Issuing…' : 'Issue trial'}</Button>
            <Button variant="ghost" onClick={onClose}>
              <X size={14} /> Cancel
            </Button>
          </div>
        </div>
      )}
    </Drawer>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>{label}</div>
      <div style={{
        padding: '8px 10px', background: '#0f1115', border: '1px solid #1f2937',
        borderRadius: 4, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12,
        wordBreak: 'break-all',
      }}>{value}</div>
    </div>
  );
}

function Input({
  label, value, onChange, placeholder, type = 'text',
}: { label: string; value: string; onChange: (v: string) => void; placeholder?: string; type?: string }) {
  return (
    <label style={{ display: 'block', fontSize: 13 }}>
      <div style={{ marginBottom: 4, color: '#cbd5e1' }}>{label}</div>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: '100%',
          padding: '8px 10px',
          background: '#0f1115',
          border: '1px solid #1f2937',
          borderRadius: 4,
          color: '#e2e8f0',
          fontFamily: 'inherit',
          fontSize: 13,
        }}
      />
    </label>
  );
}

function Select({
  label, value, onChange, children,
}: { label: string; value: string; onChange: (v: string) => void; children: React.ReactNode }) {
  return (
    <label style={{ display: 'block', fontSize: 13 }}>
      <div style={{ marginBottom: 4, color: '#cbd5e1' }}>{label}</div>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: '100%',
          padding: '8px 10px',
          background: '#0f1115',
          border: '1px solid #1f2937',
          borderRadius: 4,
          color: '#e2e8f0',
          fontFamily: 'inherit',
          fontSize: 13,
        }}
      >{children}</select>
    </label>
  );
}
